// TodayAttendanceDashboard.jsx - WITH SKELETON LOADING & SPECIFIC CLASSNAMES
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTodaySummary, fetchLiveAttendance } from '../features/attendance/attendanceThunks';
import {
  selectTodaySummary,
  selectTodayLoading,
  selectTodayError,
  selectLiveAttendance,
  selectLiveLoading,
} from '../features/attendance/attendanceSlice';
import '../CSS/TodayAttendanceDashboard.css';
import Sidebar from './Sidebar';

// Skeleton Components
const SkeletonCard = () => (
  <div className="today-skeleton-card">
    <div className="today-skeleton-number"></div>
    <div className="today-skeleton-text"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="today-skeleton-row">
    <td><div className="today-skeleton-cell"></div></td>
    <td><div className="today-skeleton-cell"></div></td>
    <td><div className="today-skeleton-cell"></div></td>
    <td><div className="today-skeleton-cell"></div></td>
    <td><div className="today-skeleton-cell"></div></td>
    <td><div className="today-skeleton-cell"></div></td>
  </tr>
);

const TodayAttendanceDashboard = () => {
  const dispatch = useDispatch();
  const todaySummary = useSelector(selectTodaySummary);
  const loading = useSelector(selectTodayLoading);
  const error = useSelector(selectTodayError);
  const liveAttendance = useSelector(selectLiveAttendance);
  const liveLoading = useSelector(selectLiveLoading);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'live'

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchTodaySummary());
    dispatch(fetchLiveAttendance());
  }, [dispatch]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchTodaySummary());
      dispatch(fetchLiveAttendance());
    }, 60000);
    
    return () => clearInterval(interval);
  }, [dispatch]);

  // Manual refresh
  const handleRefresh = () => {
    dispatch(fetchTodaySummary());
    dispatch(fetchLiveAttendance());
  };

  // Filter records
  const filteredRecords = todaySummary.records.filter(record => {
    const matchesSearch = record.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.employee_code.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'ALL' || record.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Format time
  const formatTime = (datetime) => {
    if (!datetime) return '--:--';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusMap = {
      'PRESENT': 'today-status-present',
      'LATE': 'today-status-late',
      'ABSENT': 'today-status-absent',
      'HALF_DAY': 'today-status-halfday',
      'LEAVE': 'today-status-leave',
      'MEDICAL_LEAVE': 'today-status-medical',
    };
    return statusMap[status] || 'today-status-default';
  };

  if (loading && !todaySummary.records.length) {
    return (
      <div className='index-box'>
        <Sidebar />
        <div className="today-attendance-dashboard">
          {/* Skeleton Header */}
          <div className="today-dashboard-header">
            <div className="today-header-left">
              <div className="today-skeleton-title"></div>
              <div className="today-skeleton-subtitle"></div>
            </div>
            <div className="today-header-right">
              <div className="today-skeleton-button"></div>
            </div>
          </div>

          {/* Skeleton Summary Cards */}
          <div className="today-summary-cards">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>

          {/* Skeleton Tabs */}
          <div className="today-tabs-container">
            <div className="today-skeleton-tab"></div>
            <div className="today-skeleton-tab"></div>
          </div>

          {/* Skeleton Table */}
          <div className="today-attendance-table-container">
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => <SkeletonTableRow key={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='index-box'>
        <Sidebar />
        <div className="today-attendance-dashboard">
          <div className="today-error-container">
            <p className="today-error-message">{error}</p>
            <button onClick={handleRefresh} className="today-btn-retry">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="today-attendance-dashboard">
        {/* Header */}
        <div className="today-dashboard-header">
          <div className="today-header-left">
            <h1>Today's Attendance</h1>
            <p className="today-date-display">
              {todaySummary.date ? new Date(todaySummary.date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="today-header-right">
            <button 
              onClick={handleRefresh} 
              className="today-btn-refresh"
              disabled={loading}
            >
              <span className={loading ? 'today-refreshing' : ''}>↻</span> Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="today-summary-cards">
          <div className="today-summary-card today-card-total">
            <div className="today-card-content">
              <h3>{todaySummary.totalEmployees}</h3>
              <p>Total Employees</p>
            </div>
          </div>

          <div className="today-summary-card today-card-present">
            <div className="today-card-content">
              <h3>{todaySummary.present}</h3>
              <p>Present</p>
            </div>
          </div>

          <div className="today-summary-card today-card-late">
            <div className="today-card-content">
              <h3>{todaySummary.late}</h3>
              <p>Late</p>
            </div>
          </div>

          <div className="today-summary-card today-card-absent">
            <div className="today-card-content">
              <h3>{todaySummary.employeesWithoutRecords}</h3>
              <p>Not Checked In</p>
            </div>
          </div>

          <div className="today-summary-card today-card-leave">
            <div className="today-card-content">
              <h3>{todaySummary.onLeave}</h3>
              <p>On Leave</p>
            </div>
          </div>

          <div className="today-summary-card today-card-live">
            <div className="today-card-content">
              <h3>{liveAttendance.length}</h3>
              <p>Currently In Office</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="today-tabs-container">
          <button 
            className={`today-tab-btn ${activeTab === 'all' ? 'today-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Attendance ({todaySummary.total})
          </button>
          <button 
            className={`today-tab-btn ${activeTab === 'live' ? 'today-active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            Live - In Office ({liveAttendance.length})
          </button>
        </div>

        {/* Filters - Only show for 'all' tab */}
        {activeTab === 'all' && (
          <div className="today-filters-section">
            <div className="today-search-box">
              <input
                type="text"
                placeholder="Search by name or employee code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="today-search-input"
              />
            </div>

            <div className="today-filter-buttons">
              <button 
                className={`today-filter-btn ${statusFilter === 'ALL' ? 'today-active' : ''}`}
                onClick={() => setStatusFilter('ALL')}
              >
                All ({todaySummary.total})
              </button>
              <button 
                className={`today-filter-btn ${statusFilter === 'PRESENT' ? 'today-active' : ''}`}
                onClick={() => setStatusFilter('PRESENT')}
              >
                Present ({todaySummary.present})
              </button>
              <button 
                className={`today-filter-btn ${statusFilter === 'LATE' ? 'today-active' : ''}`}
                onClick={() => setStatusFilter('LATE')}
              >
                Late ({todaySummary.late})
              </button>
              <button 
                className={`today-filter-btn ${statusFilter === 'ABSENT' ? 'today-active' : ''}`}
                onClick={() => setStatusFilter('ABSENT')}
              >
                Absent ({todaySummary.absent})
              </button>
            </div>
          </div>
        )}

        {/* Attendance Tables */}
        {activeTab === 'all' ? (
          <div className="today-attendance-table-container">
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td>{record.employee_code}</td>
                      <td className="today-employee-name">{record.employee_name}</td>
                      <td>{formatTime(record.punch_in)}</td>
                      <td>{formatTime(record.punch_out)}</td>
                      <td>
                        {record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}
                      </td>
                      <td>
                        <span className={`today-status-badge ${getStatusClass(record.status)}`}>
                          {record.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="today-no-data">
                      {searchTerm || statusFilter !== 'ALL' 
                        ? 'No records found matching your filters' 
                        : 'No attendance records for today'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="today-attendance-table-container">
            <div className="today-live-header">
              <h3>Employees Currently In Office</h3>
              <span className="today-live-indicator">
                <span className="today-pulse"></span> Live
              </span>
            </div>
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Check In Time</th>
                  <th>Hours So Far</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {liveLoading ? (
                  [...Array(3)].map((_, i) => <SkeletonTableRow key={i} />)
                ) : liveAttendance.length > 0 ? (
                  liveAttendance.map((record) => (
                    <tr key={record.id} className="today-live-row">
                      <td>{record.employee_code}</td>
                      <td className="today-employee-name">{record.employee_name}</td>
                      <td>{formatTime(record.punch_in)}</td>
                      <td>
                        {record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}
                      </td>
                      <td>
                        <span className={`today-status-badge ${getStatusClass(record.status)}`}>
                          {record.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="today-no-data">
                      No employees currently in office
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Stats */}
        <div className="today-footer-stats">
          <div className="today-stat-item">
            <span className="today-stat-label">Total Work Hours:</span>
            <span className="today-stat-value">{todaySummary.totalWorkHours.toFixed(2)} hrs</span>
          </div>
          <div className="today-stat-item">
            <span className="today-stat-label">Average Work Hours:</span>
            <span className="today-stat-value">{todaySummary.avgWorkHours.toFixed(2)} hrs</span>
          </div>
          <div className="today-stat-item">
            <span className="today-stat-label">Attendance Rate:</span>
            <span className="today-stat-value">
              {todaySummary.totalEmployees > 0 
                ? ((todaySummary.employeesWithRecords / todaySummary.totalEmployees) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayAttendanceDashboard;
